import { useAuth } from '@services/auth'
import { useMineInfo } from '@services/auth/useMineInfo'
import { useProfile } from '@services/auth/useProfile'
import { useUserAccess, useUserAlive } from '@services/user'
import env, { envStore } from '@shared/env'
import useChargeNotification from '@shared/innmaxLib/hooks/useChargeEventNotification'
import useTitle from '@shared/innmaxLib/hooks/useTitle'
import useWebVersionChecker from '@shared/innmaxLib/hooks/useWebVersionChecker'
import { RouteItem, useAppRoutes, useRoutes } from '@shared/innmaxLib/routes'
import { InnmaxMapTool } from '@shared/InnmaxMap2'
import { Exception, QueryErrorBoundary } from '@shared/innmaxUI/ErrorBoundary'
import { Button } from '@shared/innmaxUI/Form'
import { PageHeader } from '@shared/innmaxUI/Layout'
import { Loading } from '@shared/innmaxUI/Loading'
import GlobalStyles from '@theme/GlobalStyles'
import { Layout } from 'antd'
import { contains, path } from 'ramda'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Switch, useHistory } from 'react-router-dom'
import Navbar from 'src/components/Navbar'
import SideMenu from 'src/components/SideMenu'
import Dashboard from 'src/pages/Dashboard'
import LineNotifyOauthEndpoint from 'src/pages/LineNotifyOauthEndpoint'
import Login from 'src/pages/Login'
import styled from 'styled-components'
import ChangePasswordModal from './components/ChangePasswordModal'
import * as pages from './pages'
import WeatherWebViewManagement from './pages/WeatherWebViewManagement'

const { compose } = require('ramda')

function App() {
  const { profile, isFetching } = useProfile()

  useTitle(profile?.name ?? '')

  useGoogleTranslate()

  useChargeNotification()

  const { hasNewVersion } = useWebVersionChecker()

  const {
    refetch: getUserMineInfo,
    isFetched,
    isLoading,
  } = useMineInfo({
    displayError: false,
  })

  const { isAuthenticated, isMobile, layoutStyle, renderRoute } = useAppRoutes()

  const [, handleUserAccess] = useUserAccess()

  const [, handleUserAlive] = useUserAlive()

  const history = useHistory()

  const { t } = useTranslation()

  const { _routes, routeByUrl } = useRoutes(pages)

  React.useEffect(() => {
    InnmaxMapTool.setMapPlatform('MapLibre')
  }, [])

  React.useEffect(() => {
    getUserMineInfo()
  }, [isAuthenticated])

  React.useEffect(() => {
    // 如果期間使用者有在活動(例如切換畫面移動滑鼠, 打字等)
    if (!isAuthenticated) {
      return
    }
    handleUserAccess(undefined)
  }, [isAuthenticated, history.location.pathname]) //eslint-disable-line

  React.useEffect(() => {
    // 每十分鐘打一次，確保讓使用者token不過期
    const intervalId = setInterval(() => {
      isAuthenticated && handleUserAlive(undefined)
    }, 600000)

    return () => {
      clearInterval(intervalId)
    }
  }, [isAuthenticated]) //eslint-disable-line

  useDataTitleTooltip()

  const PageTitle = compose((route: RouteItem) => {
    if (isMobile) {
      return <PageHeader title={route?.title || ''} />
    }
    if (contains(history.location.pathname, ['/', '/dashboard'])) {
      return (
        <PageHeader sub={t(`menu:dashboard`)} title={t(`menu:/dashboard`)} />
      )
    }

    if (contains(history.location.pathname, ['/media-content-management'])) {
      return <PageHeader title={t('menu:media content mgmt')} />
    }

    if (!route) {
      const subRoute = Object.keys(routeByUrl).find(
        x => history.location.pathname.indexOf(x) > -1
      )

      if (subRoute) {
        const sub: any = path([subRoute])(routeByUrl)
        return <PageHeader sub={sub.parentName} title={sub.title} />
      }
      return null
    }

    return (
      <PageHeader
        sub={t(`menu:${route.mainKey}`)}
        title={t(`menu:${route.url}`) || route.title}
      />
    )
  }, path([history.location.pathname]))(routeByUrl)

  return (
    <QueryErrorBoundary>
      <GlobalStyles />
      <ResetPassword />
      {hasNewVersion ? (
        <NewVersionHint>
          有新版本，請重新整理
          <Button
            className="style-button"
            ghost
            onClick={() => window.location.reload()}>
            重新整理
          </Button>
        </NewVersionHint>
      ) : null}
      {!isFetched && isLoading && (
        <Layout style={{ height: '100vh' }}>
          <Loading />
        </Layout>
      )}
      {!isAuthenticated && isFetched && (
        <Switch>
          <Route
            exact
            path="/lineNotify/oauth/endpoint"
            component={LineNotifyOauthEndpoint}
          />
          <Route
            exact
            path="/WeatherWebViewManagement"
            component={WeatherWebViewManagement}
          />
          <Route exact path="/evccidList" component={pages.EvccidList} />
          <Switch>
            <Route
              component={() => (
                <Layout>
                  <Login />
                </Layout>
              )}
            />
          </Switch>
        </Switch>
      )}
      {isAuthenticated && isFetched && (
        <Switch>
          <Route exact path="/login" component={Login} />
          <Route
            exact
            path="/lineNotify/oauth/endpoint"
            component={LineNotifyOauthEndpoint}
          />
          <Route
            exact
            path="/WeatherWebViewManagement"
            component={WeatherWebViewManagement}
          />
          <Route exact path="/evccidList" component={pages.EvccidList} />
          <>
            <Layout style={layoutStyle(isMobile)}>
              <LayoutStyled className="overflow-hidden">
                <div className="background-image pointer-events-none"></div>
                {isMobile ? null : <SideMenu />}
                <Layout.Content className="layout-content">
                  <Suspense fallback={<Loading />}>
                    <Navbar title={PageTitle} />
                    <Switch>
                      <Route
                        exact
                        path="/"
                        component={isMobile ? pages.MapDashboard : Dashboard}
                      />
                      {renderRoute({ data: _routes })}
                      <Route component={Exception} />
                    </Switch>
                  </Suspense>
                </Layout.Content>
              </LayoutStyled>
            </Layout>
          </>
        </Switch>
      )}
    </QueryErrorBoundary>
  )
}

const LayoutStyled = styled(Layout)`
  /* border-top: 3px solid ${({ theme }) => theme.layout.borderTop}; */
  min-height: 100vh;
  background: ${({ theme }) => theme.layout.bg};
  > .layout-content {
    background: ${({ theme }) => theme.layout.content.bg};
  }

  &:has(.widgets-dashboard) {
    .background-image {
      background-image: url('${env.imageUrlPrefix}/ChargingPointDashboardBackground.png') !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      background-size: cover !important;
      position: fixed;
      left: 0;
      right: 0;
      width: 100vw;
      height: 100vh;
      -webkit-filter: blur(8px);
      -moz-filter: blur(8px);
      -o-filter: blur(8px);
      -ms-filter: blur(8px);
      filter: blur(8px);
    }
  }
`

export default App

const ResetPassword = () => {
  const { user } = useAuth()

  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    if (!user) {
      return
    }
    setOpen(user.needResetPassword ?? false)
  }, [user]) //eslint-disable-line

  return (
    <ChangePasswordModal
      title={'重設密碼'}
      closable={false}
      open={open}
      onCancel={() => setOpen(false)}
      onOk={() => setOpen(false)}
    />
  )
}

const useDataTitleTooltip = () => {
  React.useEffect(() => {
    window.tooltipEl = document.createElement('div')
    window.tooltipEl.id = 'data-title-tooptip'
    document.body.appendChild(window.tooltipEl)

    const handleMouseOver = (event: MouseEvent) => {
      const loopParentWithAttr = (target: any): HTMLElement | null => {
        if (target?.dataset?.title) {
          return target
        }
        if (target?.parentNode) {
          return loopParentWithAttr(target.parentNode)
        }
        return null
      }
      const attrTarget = loopParentWithAttr(event.target)
      let title = attrTarget?.dataset?.title
      if (!title) {
        window.tooltipEl.style.display = 'none'
        window.tooltipTitle = ''
      } else if (title !== window.tooltipTitle && attrTarget) {
        window.tooltipTitle = title
        window.tooltipEl.textContent = title
        window.tooltipEl.style.display = 'block'
        window.tooltipEl.style.padding = '5px'
        window.tooltipEl.style.position = 'absolute'
        window.tooltipEl.style.top = '100px'
        window.tooltipEl.style.left = '100px'
        window.tooltipEl.style.backgroundColor = '#7c7c7c'
        window.tooltipEl.style.zIndex = '999'
        window.tooltipEl.style.fontSize = '0.8rem'
        window.tooltipEl.style.borderRadius = '5px'
        window.tooltipEl.style.color = '#fff'

        const targetRect = attrTarget.getBoundingClientRect()
        window.tooltipEl.style.top = `${
          targetRect.top + window.tooltipEl.offsetHeight + 5
        }px`
        window.tooltipEl.style.left = `${targetRect.left}px`
        window.tooltipEl.style.visibility = 'hidden'
        // 延遲 500 ms 顯示，先讓 Google 翻譯完成在顯示
        setTimeout(() => {
          window.tooltipEl.style.visibility = 'visible'
        }, 500)
      }
    }

    document.addEventListener('mouseover', handleMouseOver)

    return () => {
      document.removeEventListener('mouseover', handleMouseOver)
    }
  }, [])
}

const useGoogleTranslate = () => {
  const { profile, isFetching } = useProfile()

  const comboTranslate = React.useRef<null | HTMLInputElement>(null)

  const googleTranslateElementInit = React.useCallback(() => {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'zh-TW',
      },
      'google_translate_element'
    )
    const elementDetector = () => {
      const el: HTMLInputElement | null = document.querySelector(
        '#google_translate_element .goog-te-combo'
      )
      if (el) {
        comboTranslate.current = el
        profile.language &&
          setTimeout(() => googleTranslateLanguageChaged(profile.language))
      } else {
        setTimeout(elementDetector, 100)
      }
    }
    elementDetector()
  }, []) //eslint-disable-line

  const googleTranslateLanguageChaged = React.useCallback(
    (language: string) => {
      if (comboTranslate && comboTranslate.current) {
        comboTranslate.current.value = language || comboTranslate.current.value
        const event = new Event('change')
        comboTranslate.current.dispatchEvent(event)
      }
    },
    [comboTranslate]
  )

  React.useEffect(() => {
    if (envStore.isGoogleTranslateDisabled) {
      return
    }
    const addScript = document.createElement('script')
    addScript.setAttribute(
      'src',
      '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    )
    document.body.appendChild(addScript)
    window.googleTranslateElementInit = googleTranslateElementInit
  }, [googleTranslateElementInit]) //eslint-disable-line

  React.useEffect(() => {
    profile.language &&
      !envStore.isGoogleTranslateDisabled &&
      setTimeout(() => googleTranslateLanguageChaged(profile.language))
  }, [profile.language]) //eslint-disable-line
}

const NewVersionHint = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  background-color: rgb(0, 0, 0, 0.65);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  padding: 15px 0;
  .style-button {
    opacity: 0.8;
    position: absolute;
    right: 10px;
    padding: 0 10px;
    color: #fff;
    border: 1px solid #fff;
    background-color: transparent;
    &:hover,
    &:focus,
    &:active {
      background-color: #fff;
      color: ${({ theme }) => theme.primary};
    }
  }
`
